import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  getDocs, 
  doc, 
  getDoc,
  updateDoc, 
  setDoc, 
  serverTimestamp, 
  orderBy, 
  where 
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';

const ScholarshipManagement = () => {
  const [scholarships, setScholarships] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedScholarshipFilter, setSelectedScholarshipFilter] = useState('all');
  const navigate = useNavigate();

  // Replace single formData state with a per-scholarship formData state
  const [formData, setFormData] = useState({}); // { [scholarshipId]: { startDate, endDate } }

  const defaultScholarships = [
    {
      id: 'fellow-scholarship',
      title: 'Become a Fellow Scholarship',
      type: 'fellow',
      eligibility: 'Nigerian University Students from 2nd level until penultimate year in Education related courses',
      benefits: [
        'Year tuition cost coverage',
        'Exam stipends',
        'Opportunity to become a mentor'
      ],
      requiredDocuments: [
        'Valid Student ID',
        'Academic Transcript',
        'Letter of Recommendation (Academic)',
        'Statement of Purpose',
        'Research Proposal (pdf)'
      ]
    },
    {
      id: 'utme-scholarship',
      title: 'UTME Scholar Project',
      type: 'utme',
      eligibility: 'Students in Senior Secondary School with interest in Education related courses',
      benefits: [
        'UTME fee coverage',
        'Post UTME fee coverage',
        'First year tuition fee coverage'
      ],
      requiredDocuments: [
        'WAEC/NECO Result',
        'School ID',
        'Letter of Recommendation (Academic)',
        'Statement of Purpose',
      ]
    },
    {
      id: 'orphan-scholarship',
      title: 'Orphan Project',
      type: 'orphan',
      eligibility: 'Orphaned children in Schools in need of financial Support',
      benefits: [
        'Tuition fee coverage',
        'Educational materials support',
        'Mentorship program access'
      ],
      requiredDocuments: [
        'Death Certificate(s) of Parent(s)',
        'Guardian\'s ID',
        'School ID',
        'Academic Records',
        'Letter of Recommendation (Academic)',
      ]
    }
  ];

  // Load data based on active tab
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Always fetch scholarships as they're needed for both tabs
        await fetchScholarships();
        
        // Fetch applications for the main view
        await fetchApplications();
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []); // Fetch data once on mount

  // Fetch applications when filters change
  useEffect(() => {
    fetchApplications();
  }, [selectedStatus, selectedScholarshipFilter]);

  const fetchScholarships = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'scholarships'));
      const scholarshipData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setScholarships(scholarshipData);
      return scholarshipData;
    } catch (error) {
      console.error('Error fetching scholarships:', error);
      return [];
    }
  };

  const fetchApplications = async () => {
    try {
      // Construct query based on filters
      let applicationsQuery;
      if (selectedStatus !== 'all' && selectedScholarshipFilter !== 'all') {
        applicationsQuery = query(
          collection(db, "scholarship_applications"),
          where("status", "==", selectedStatus),
          where("scholarshipId", "==", selectedScholarshipFilter),
          orderBy("appliedAt", "desc")
        );
      } else if (selectedStatus !== 'all') {
        applicationsQuery = query(
          collection(db, "scholarship_applications"),
          where("status", "==", selectedStatus),
          orderBy("appliedAt", "desc")
        );
      } else if (selectedScholarshipFilter !== 'all') {
        applicationsQuery = query(
          collection(db, "scholarship_applications"),
          where("scholarshipId", "==", selectedScholarshipFilter),
          orderBy("appliedAt", "desc")
        );
      } else {
        applicationsQuery = query(
          collection(db, "scholarship_applications"),
          orderBy("appliedAt", "desc")
        );
      }

      const applicationsSnapshot = await getDocs(applicationsQuery);
      const applicationsList = applicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setApplications(applicationsList);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  // Update application status
  const updateApplicationStatus = async (applicationId, newStatus) => {
    try {
      await updateDoc(doc(db, "scholarship_applications", applicationId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setApplications(prev => prev.map(app => 
        app.id === applicationId 
          ? { ...app, status: newStatus, updatedAt: serverTimestamp() } 
          : app
      ));
    } catch (error) {
      console.error("Error updating application status:", error);
    }
  };

  const getScholarshipStatus = (scholarship) => {
    const now = new Date();
    const startDate = new Date(scholarship.startDate);
    const endDate = new Date(scholarship.endDate);

    if (now < startDate) return 'upcoming';
    if (now > endDate) return 'closed';
    return 'open';
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Update handleDateUpdate to accept per-scholarship formData
  const handleDateUpdate = async (scholarshipId, forceClose = false, newEndDate = null, customFormData = null) => {
    try {
      const docRef = doc(db, 'scholarships', scholarshipId);
      const scholarshipData = defaultScholarships.find(s => s.id === scholarshipId);
      let updateData = {
        ...scholarshipData,
        updatedAt: new Date().toISOString()
      };
      if (forceClose) {
        updateData.endDate = new Date().toISOString().slice(0, 10); // today
      } else if (newEndDate) {
        updateData.endDate = newEndDate;
      } else if (customFormData) {
        updateData.startDate = customFormData.startDate;
        updateData.endDate = customFormData.endDate;
      }
      await setDoc(docRef, updateData, { merge: true });
      fetchScholarships();
      setFormData(prev => ({ ...prev, [scholarshipId]: { startDate: '', endDate: '' } }));
    } catch (error) {
      console.error('Error updating dates:', error);
    }
  };

  if (loading && scholarships.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[rgb(130,88,18)]"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Scholarship Management</h1>
      
      {/* Add View All Applications Button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/scholarship-applications')}
          className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded-md hover:bg-[rgb(110,68,0)] transition-colors duration-300 flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
          </svg>
          View All Applications
        </button>
      </div>
      
      {/* Scholarship List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {defaultScholarships.map(scholarship => {
          const scholarshipData = scholarships.find(s => s.id === scholarship.id) || {};
          const status = getScholarshipStatus(scholarshipData);
          const thisForm = formData[scholarship.id] || { startDate: '', endDate: '' };
          const hasDates = !!(scholarshipData.startDate && scholarshipData.endDate);
          return (
            <div key={scholarship.id} className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold mb-4">{scholarship.title}</h2>
              <div className="mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  status === 'open' ? 'bg-green-100 text-green-800' :
                  status === 'upcoming' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    value={thisForm.startDate}
                    onChange={e => setFormData(prev => ({ ...prev, [scholarship.id]: { ...thisForm, startDate: e.target.value } }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(130,88,18)] focus:ring-[rgb(130,88,18)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    value={thisForm.endDate}
                    onChange={e => setFormData(prev => ({ ...prev, [scholarship.id]: { ...thisForm, endDate: e.target.value } }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(130,88,18)] focus:ring-[rgb(130,88,18)]"
                  />
                </div>
                <button
                  onClick={() => handleDateUpdate(scholarship.id, false, null, thisForm)}
                  className="w-full bg-[rgb(130,88,18)] text-white py-2 px-4 rounded-md hover:bg-[rgb(110,75,15)] transition-colors duration-300"
                >
                  {hasDates ? 'Update Dates' : 'Create Dates'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {/* END: Existing Scholarship List */}

      {/* ACTIVE SCHOLARSHIPS MANAGEMENT SECTION */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Active Scholarships</h2>
        <ActiveScholarshipsSection 
          scholarships={scholarships}
          applications={applications}
          onForceClose={async (scholarshipId) => {
            await handleDateUpdate(scholarshipId, true);
          }}
          onExtendDates={async (scholarshipId, newEndDate) => {
            await handleDateUpdate(scholarshipId, false, newEndDate);
          }}
        />
      </div>
      
      {/* Scholarship List */}
      {/* Filters */}
      {/* Applications List */}
      
    </div>
  );
};

export default ScholarshipManagement; 

const ActiveScholarshipsSection = ({ scholarships, applications, onForceClose, onExtendDates }) => {
  const [extendDate, setExtendDate] = useState({});

  // Filter only open scholarships
  const now = new Date();
  const activeScholarships = scholarships.filter(s => {
    if (!s.startDate || !s.endDate) return false;
    const start = new Date(s.startDate);
    const end = new Date(s.endDate);
    return now >= start && now <= end;
  });

  // Helper to count applications and statuses
  const getAppStats = (scholarshipId) => {
    const filtered = applications.filter(app => app.scholarshipId === scholarshipId);
    const total = filtered.length;
    const statusCount = {};
    filtered.forEach(app => {
      statusCount[app.status] = (statusCount[app.status] || 0) + 1;
    });
    return { total, statusCount };
  };

  if (activeScholarships.length === 0) {
    return <div className="text-gray-500">No active scholarships at the moment.</div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {activeScholarships.map(sch => {
        const { total, statusCount } = getAppStats(sch.id);
        const minDate = sch.endDate ? new Date(sch.endDate).toISOString().slice(0, 10) : '';
        return (
          <div key={sch.id} className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-bold mb-2">{sch.title}</h3>
            <div className="mb-2">
              <span className="text-sm font-medium">Start Date: </span>
              <span>{sch.startDate ? new Date(sch.startDate).toLocaleDateString() : 'N/A'}</span>
            </div>
            <div className="mb-2">
              <span className="text-sm font-medium">End Date: </span>
              <span>{sch.endDate ? new Date(sch.endDate).toLocaleDateString() : 'N/A'}</span>
            </div>
            <div className="mb-2">
              <span className="text-sm font-medium">Applicants: </span>
              <span>{total}</span>
            </div>
            <div className="mb-2">
              <span className="text-sm font-medium">Status Count: </span>
              <ul className="ml-4 list-disc">
                {Object.entries(statusCount).map(([status, count]) => (
                  <li key={status}>{status}: {count}</li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col gap-2 mt-4">
              <button
                className="bg-red-600 text-white py-1 px-3 rounded hover:bg-red-700"
                onClick={() => onForceClose(sch.id)}
              >
                Force Close
              </button>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="date"
                  value={extendDate[sch.id] || minDate}
                  min={minDate}
                  onChange={e => setExtendDate(prev => ({ ...prev, [sch.id]: e.target.value }))}
                  className="border rounded px-2 py-1"
                />
                <button
                  className="bg-blue-600 text-white py-1 px-3 rounded hover:bg-blue-700"
                  onClick={() => onExtendDates(sch.id, extendDate[sch.id])}
                  disabled={!extendDate[sch.id] || extendDate[sch.id] <= minDate}
                >
                  Extend End Date
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}; 